import { FC } from 'react'
import styled from 'styled-components'
import { media } from '../../../theme/breakpoints'
import { PrimaryButton } from '../../buttons/primaryButton'
import { SecondaryButton } from '../../buttons/secondaryButton'
import { Header1, Header3 } from '../../text/headers'
import { Text } from '../../text/textStyles'

const Background = styled.div<{ $highlight?: boolean }>`
    width: 421px;
    height: fit-content;
    padding: 3px;
    border-radius: 10px;
    box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.15);

    background: ${(p) =>
        p.$highlight
            ? 'linear-gradient(149.08deg,#f5faf9 -15.23%,#b3d9c8 67.18%,#359a68 137.26%)'
            : 'white'};
    transition: all 1s linear;

    ${media.mobile} {
        width: 100%;
        max-width: 421px;
    }
`

const Container = styled.div`
    display: flex;
    flex-direction: column;
    padding: 32px 0;
    border-radius: 8px;
    position: relative;
    background: white;
`

const PriceRow = styled.div`
    display: flex;
    align-items: baseline;
    margin-top: 16px;
    margin-bottom: 16px;
    column-gap: 8px;
`

const OfferContainer = styled(Text)`
    display: flex;
    column-gap: 8px;
    align-items: center;
`

const TopSection = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 24px;
`

const BottomSection = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 16px;
    row-gap: 16px;
`

const TickIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
    >
        <path
            d="M12.72 6.79L8.43 11.09L6.78 9.44C6.69036 9.3353 6.58004 9.2503 6.45597 9.1903C6.33191 9.1303 6.19678 9.0965 6.05906 9.0912C5.92134 9.0859 5.78401 9.1091 5.65568 9.1594C5.52736 9.2096 5.41081 9.2859 5.31335 9.3833C5.2159 9.4808 5.13964 9.5974 5.08937 9.7257C5.03909 9.854 5.01589 9.9913 5.02121 10.1291C5.02653 10.2668 5.06026 10.4019 5.12028 10.526C5.1803 10.65 5.26532 10.7604 5.37 10.85L7.72 13.21C7.81344 13.3027 7.92426 13.376 8.0461 13.4258C8.1679 13.4755 8.2984 13.5008 8.43 13.5C8.6923 13.4989 8.9437 13.3947 9.13 13.21L14.13 8.21C14.2237 8.117 14.2981 8.0064 14.3489 7.88458C14.3997 7.76272 14.4258 7.63201 14.4258 7.5C14.4258 7.36799 14.3997 7.23728 14.3489 7.11542C14.2981 6.99356 14.2237 6.88296 14.13 6.79C13.9426 6.60375 13.6892 6.49921 13.425 6.49921C13.1608 6.49921 12.9074 6.60375 12.72 6.79ZM10 0C8.0222 0 6.08879 0.58649 4.4443 1.6853C2.79981 2.78412 1.51809 4.3459 0.761211 6.17317C0.00433075 8.0004 -0.193699 10.0111 0.192151 11.9509C0.578001 13.8907 1.53041 15.6725 2.92894 17.0711C4.32746 18.4696 6.10929 19.422 8.0491 19.8079C9.9889 20.1937 11.9996 19.9957 13.8268 19.2388C15.6541 18.4819 17.2159 17.2002 18.3147 15.5557C19.4135 13.9112 20 11.9778 20 10C20 8.6868 19.7413 7.38642 19.2388 6.17317C18.7363 4.95991 17.9997 3.85752 17.0711 2.92893C16.1425 2.00035 15.0401 1.26375 13.8268 0.7612C12.6136 0.25866 11.3132 0 10 0ZM10 18C8.4178 18 6.87104 17.5308 5.55544 16.6518C4.23985 15.7727 3.21447 14.5233 2.60897 13.0615C2.00347 11.5997 1.84504 9.9911 2.15372 8.4393C2.4624 6.88743 3.22433 5.46197 4.34315 4.34315C5.46197 3.22433 6.88743 2.4624 8.4393 2.15372C9.9911 1.84504 11.5997 2.00346 13.0615 2.60896C14.5233 3.21447 15.7727 4.23984 16.6518 5.55544C17.5308 6.87103 18 8.4177 18 10C18 12.1217 17.1572 14.1566 15.6569 15.6569C14.1566 17.1571 12.1217 18 10 18Z"
            fill="#0A8447"
        />
    </svg>
)

const Divider = styled.div`
    width: 100%;
    margin: 24px 0;
    padding: 16px 0;
`

const DividerLine = styled.div`
    width: 100%;
    height: 1px;
    background: #cccbcb;
`

const Offer: FC<{ children: string }> = ({ children }) => {
    return (
        <OfferContainer size="small" $weight="regular">
            <TickIcon />
            {children}
        </OfferContainer>
    )
}

interface Props {
    name: string
    targetCustomer: string
    cost: string
    frequency: string
    selected?: boolean
    offers: string[]
    disabled?: boolean
    onUpgrade?: () => void
    highlight?: boolean
}

export const SubscriptionCard: FC<Props> = (props) => {
    return (
        <Background $highlight={props.highlight}>
            <Container>
                <TopSection>
                    <Header3>{props.name}</Header3>
                    <Text size="medium" $weight="regular" color="secondary">
                        {props.targetCustomer}
                    </Text>
                    <PriceRow>
                        <Header1>{props.cost}</Header1>
                        <Text size="small" $weight="regular">
                            {props.frequency}
                        </Text>
                    </PriceRow>

                    {props.highlight ? (
                        <PrimaryButton
                            height="large"
                            width="large"
                            onClick={props.onUpgrade}
                        >
                            Upgrade
                        </PrimaryButton>
                    ) : (
                        <SecondaryButton
                            height="large"
                            width="large"
                            onClick={props.onUpgrade}
                        >
                            Upgrade
                        </SecondaryButton>
                    )}
                </TopSection>
                <Divider>
                    <DividerLine />
                </Divider>
                <BottomSection>
                    {props.offers.map((offer, index) => {
                        return <Offer key={index}>{offer}</Offer>
                    })}
                </BottomSection>
            </Container>
        </Background>
    )
}
